import React, { useState, useEffect } from "react";
import Sidebar from "../mini-comp/Sidebar";
import Topbar from "../mini-comp/Topbar";
import axios from "axios";
import Cookies from "js-cookie";
import API_BASE_URL from "../../../config/api";
import Swal from "sweetalert2";
import { RiEyeLine, RiEditLine, RiDeleteBinLine } from "react-icons/ri";

const HRManagers = () => {
  const [hrManagers, setHrManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewingManager, setViewingManager] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    company: "",
  });

  const fetchHRManagers = () => {
    const token = Cookies.get("token");
    if (!token) {
      console.error("No token found in cookies");
      setLoading(false);
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/v1/hr/managers/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("HR Managers response:", response.data);
        setHrManagers(response.data.hr_managers || response.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching HR managers:", error);
        // Fallback: try fetching from dashboard endpoint
        axios
          .get(`${API_BASE_URL}/api/v1/billing/hr-managers/`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            setHrManagers(response.data.hr_managers || []);
            setLoading(false);
          })
          .catch((err) => {
            console.error("Error fetching HR managers from dashboard:", err);
            setLoading(false);
          });
      });
  };

  const fetchCompanies = () => {
    const token = Cookies.get("token");
    if (!token) {
      console.error("No token found in cookies");
      return;
    }

    setLoadingCompanies(true);
    axios
      .get(`${API_BASE_URL}/api/v1/billing/companies/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("Companies response:", response.data);
        const companiesData = response.data.companies || response.data || [];
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        setLoadingCompanies(false);
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
        setLoadingCompanies(false);
      });
  };

  const handleInputChange = (e) => {
    const { id, name, value } = e.target;
    const fieldName = name || id;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleModalOpen = () => {
    // Refresh companies when modal opens to ensure fresh data
    if (companies.length === 0) {
      fetchCompanies();
    }
  };

  const handleViewManager = (manager) => {
    setViewingManager(manager);
  };

  const handleEdit = (manager) => {
    setEditingManager(manager);
    setIsEditMode(true);
    setFormData({
      first_name: manager.first_name || "",
      last_name: manager.last_name || "",
      email: manager.email || "",
      phone_number: manager.phone || manager.phone_number || "",
      company: manager.company_id || manager.company?.id || "",
    });
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    setEditingManager(null);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      company: "",
    });
  };

  const handleDelete = async (manager) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${manager.full_name || manager.name || `${manager.first_name} ${manager.last_name}`}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    // If user confirmed, proceed with deletion
    if (result.isConfirmed) {
      const token = Cookies.get('token');
      if (!token) {
        console.error('No token found in cookies');
        return;
      }

      try {
        await axios.delete(`${API_BASE_URL}/api/v1/employees/${manager.employee_id || manager.user_id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'HR Manager has been deleted successfully.',
          confirmButtonText: 'OK'
        });

        // Refresh HR managers list
        fetchHRManagers();
      } catch (error) {
        console.error('Error deleting HR manager:', error);

        // Show error message
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to delete HR manager. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  useEffect(() => {
    const token = Cookies.get("token");
    if (!token) {
      console.error("No token found in cookies");
      setLoading(false);
      return;
    }

    // Fetch HR Managers
    fetchHRManagers();

    // Fetch Companies
    fetchCompanies();
  }, []);

  const getStatusBadge = (status) => {
    if (status === "active" || status === "Active") {
      return <span className="badge bg-label-success">Active</span>;
    }
    return <span className="badge bg-label-danger">Inactive</span>;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const token = Cookies.get("token");
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No token found in cookies",
      });
      setSubmitting(false);
      return;
    }

    try {
      // Ensure company_id is sent as a number
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        company: parseInt(formData.company, 10),
        role: "hr_manager", // Assuming HR managers have this role
      };

      // Validate company is a valid number
      if (!submitData.company || isNaN(submitData.company)) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please select a valid company",
        });
        setSubmitting(false);
        return;
      }

      let response;
      if (isEditMode && editingManager) {
        // Update existing HR manager
        response = await axios.put(
          `${API_BASE_URL}/api/v1/employees/${editingManager.employee_id || editingManager.user_id}/`,
          submitData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("HR Manager updated successfully:", response.data);
      } else {
        // Create new HR manager
        response = await axios.post(
          `${API_BASE_URL}/api/v1/employees/`,
          submitData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("HR Manager created successfully:", response.data);
      }

      // Close modal
      const modalId = isEditMode ? "editHRManager" : "addNewHRManager";
      const modal = document.getElementById(modalId);
      if (modal) {
        const bsModal = window.bootstrap?.Modal?.getInstance(modal);
        if (bsModal) {
          bsModal.hide();
        } else {
          // Fallback if Bootstrap modal instance not available
          modal.classList.remove("show");
          modal.style.display = "none";
          document.body.classList.remove("modal-open");
          const backdrop = document.querySelector(".modal-backdrop");
          if (backdrop) backdrop.remove();
        }
      }

      // Reset form
      handleCloseModal();

      // Refresh HR managers list
      fetchHRManagers();

      setSubmitting(false);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: isEditMode ? "HR Manager updated successfully!" : "HR Manager added successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} HR manager:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          `Failed to ${isEditMode ? 'update' : 'add'} HR manager. Please try again.`,
      });
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Topbar />
      <Sidebar />

      <>
        {/* app-content-area-start */}
        <div className="app-content-area">
          <div className="app-content-wrap">
            <div className="container-fluid">
              <div className="row">
                <div className="col-xl-12">
                  <div className="card">
                    <div className="card-header justify-between">
                      <h4 className="d-flex-items gap-10">
                        HR Managers
                        <span className="badge bg-label-primary">
                          {hrManagers.length}
                        </span>
                      </h4>
                      <div className="d-flex flex-wrap gap-15">
                        <button
                          type="button"
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#addNewHRManager"
                          onClick={handleModalOpen}
                        >
                          Add HR Manager
                        </button>
                        <a
                          className="btn btn-success text-white"
                          href="javascript:void(0);"
                        >
                          Export As Excel
                        </a>
                      </div>
                    </div>
                    <div className="card-body pt-15">
                      <div className="table-responsive">
                        <table
                          id="dataTableDefault"
                          className="table text-nowrap w-100"
                        >
                          <thead>
                            <tr>
                              <th>HR Manager Name</th>
                              <th>Company</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <span className="text-muted">Loading...</span>
                                </td>
                              </tr>
                            ) : hrManagers && hrManagers.length > 0 ? (
                              hrManagers.map((hrManager) => (
                                <tr key={hrManager.employee_id || hrManager.user_id}>
                                  <td className="d-flex-items gap-10">
                                    <div className="avatar radius-100">
                                      <img
                                        className="radius-100"
                                        src={
                                          hrManager.profile_picture ||
                                          "assets/images/avatar/avatar-thumb-001.webp"
                                        }
                                        alt={
                                          hrManager.full_name ||
                                          hrManager.name ||
                                          "HR Manager"
                                        }
                                      />
                                    </div>
                                    <h6>
                                      <a
                                      >
                                        {hrManager.full_name ||
                                          hrManager.name ||
                                          `${hrManager.first_name || ""} ${hrManager.last_name || ""
                                            }`.trim() ||
                                          "N/A"}
                                      </a>
                                    </h6>
                                  </td>
                                  <td>
                                    {hrManager.company_name ||
                                      hrManager.company?.name ||
                                      hrManager.company ||
                                      "N/A"}
                                  </td>
                                  <td>{hrManager.email || "N/A"}</td>
                                  <td>
                                    {hrManager.phone ||
                                      hrManager.phone_number ||
                                      "N/A"}
                                  </td>
                                  <td>{getStatusBadge(hrManager.status)}</td>
                                  <td>
                                    <div className="d-flex-items gap-5">
                                      <button
                                        className="btn-icon btn-success-light"
                                        type="button"
                                        data-bs-toggle="modal"
                                        data-bs-target="#viewHRManager"
                                        onClick={() => handleViewManager(hrManager)}
                                      >
                                        <RiEyeLine />
                                      </button>
                                      <button
                                        className="btn-icon btn-info-light"
                                        type="button"
                                        data-bs-toggle="modal"
                                        data-bs-target="#editHRManager"
                                        onClick={() => handleEdit(hrManager)}
                                      >
                                        <RiEditLine />
                                      </button>
                                      <button
                                        className="btn-icon btn-danger-light"
                                        type="button"
                                        onClick={() => handleDelete(hrManager)}
                                      >
                                        <RiDeleteBinLine />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <span className="text-muted">
                                    No HR Managers found
                                  </span>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <>
                {/* start create Employee model */}
                <div
                  className="modal fade"
                  id="addNewHRManager"
                  tabIndex={-1}
                  aria-labelledby="addNewHRManagerLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <form onSubmit={handleSubmit}>
                        <div className="modal-header">
                          <h1
                            className="modal-title fs-16"
                            id="addNewHRManagerLabel"
                          >
                            Add New HR Manager
                          </h1>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          />
                        </div>
                        <div className="modal-body">
                          <div className="row gy-15">
                            <div className="col-xl-6">
                              <label htmlFor="first_name" className="form-label">
                                First Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="first_name"
                                placeholder="First Name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="last_name" className="form-label">
                                Last Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="last_name"
                                placeholder="Last Name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="email" className="form-label">
                                Email <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                id="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="phone_number" className="form-label">
                                Phone Number <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="phone_number"
                                placeholder="Phone Number"
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-12">
                              <label htmlFor="company" className="form-label">
                                Company <span className="text-danger">*</span>
                              </label>
                              {loadingCompanies ? (
                                <div className="form-control">
                                  <span className="text-muted">Loading companies...</span>
                                </div>
                              ) : (
                                <select
                                  className="form-select"
                                  id="company"
                                  name="company"
                                  value={formData.company}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="">Select a company</option>
                                  {companies.map((company) => (
                                    <option
                                      key={company.id}
                                      value={company.id}
                                    >
                                      {company.trade_name || company.company_name || "Unnamed Company"}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {!loadingCompanies && companies.length === 0 && (
                                <small className="text-danger">
                                  No companies available. Please add a company first.
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-danger"
                            data-bs-dismiss="modal"
                            disabled={submitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                          >
                            {submitting ? "Saving..." : "Add HR Manager"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                {/* end create Employee model */}

                {/* start edit HR Manager modal */}
                <div
                  className="modal fade"
                  id="editHRManager"
                  tabIndex={-1}
                  aria-labelledby="editHRManagerLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <form onSubmit={handleSubmit}>
                        <div className="modal-header">
                          <h1
                            className="modal-title fs-16"
                            id="editHRManagerLabel"
                          >
                            Edit HR Manager
                          </h1>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleCloseModal}
                          />
                        </div>
                        <div className="modal-body">
                          <div className="row gy-15">
                            <div className="col-xl-6">
                              <label htmlFor="edit_first_name" className="form-label">
                                First Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="edit_first_name"
                                name="first_name"
                                placeholder="First Name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="edit_last_name" className="form-label">
                                Last Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="edit_last_name"
                                name="last_name"
                                placeholder="Last Name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="edit_email" className="form-label">
                                Email <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                id="edit_email"
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled
                              />
                            </div>
                            <div className="col-xl-6">
                              <label htmlFor="edit_phone_number" className="form-label">
                                Phone Number <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="edit_phone_number"
                                name="phone_number"
                                placeholder="Phone Number"
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                            <div className="col-xl-12">
                              <label htmlFor="edit_company" className="form-label">
                                Company <span className="text-danger">*</span>
                              </label>
                              {loadingCompanies ? (
                                <div className="form-control">
                                  <span className="text-muted">Loading companies...</span>
                                </div>
                              ) : (
                                <select
                                  className="form-select"
                                  id="edit_company"
                                  name="company"
                                  value={formData.company}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="">Select a company</option>
                                  {companies.map((company) => (
                                    <option
                                      key={company.id}
                                      value={company.id}
                                    >
                                      {company.trade_name || company.company_name || "Unnamed Company"}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-danger"
                            data-bs-dismiss="modal"
                            onClick={handleCloseModal}
                            disabled={submitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                          >
                            {submitting ? "Updating..." : "Update HR Manager"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                {/* end edit HR Manager modal */}

                {/* start view HR Manager modal */}
                <div
                  className="modal fade"
                  id="viewHRManager"
                  tabIndex={-1}
                  aria-labelledby="viewHRManagerLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h1
                          className="modal-title fs-16"
                          id="viewHRManagerLabel"
                        >
                          HR Manager Details
                        </h1>
                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        />
                      </div>
                      <div className="modal-body">
                        {viewingManager && (
                          <div className="row gy-20">
                            <div className="col-xl-6">
                              <div className="mb-15">
                                <label className="form-label fw-semibold text-muted">
                                  First Name
                                </label>
                                <p className="fs-16">
                                  {viewingManager.first_name || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="col-xl-6">
                              <div className="mb-15">
                                <label className="form-label fw-semibold text-muted">
                                  Last Name
                                </label>
                                <p className="fs-16">
                                  {viewingManager.last_name || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="col-xl-6">
                              <div className="mb-15">
                                <label className="form-label fw-semibold text-muted">
                                  Email Address
                                </label>
                                <p className="fs-16">
                                  {viewingManager.email || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="col-xl-6">
                              <div className="mb-15">
                                <label className="form-label fw-semibold text-muted">
                                  Phone Number
                                </label>
                                <p className="fs-16">
                                  {viewingManager.phone ||
                                    viewingManager.phone_number ||
                                    "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="col-xl-12">
                              <div className="mb-15">
                                <label className="form-label fw-semibold text-muted">
                                  Company
                                </label>
                                <p className="fs-16">
                                  {viewingManager.company_name ||
                                    viewingManager.company?.name ||
                                    viewingManager.company ||
                                    "N/A"}
                                </p>
                              </div>
                            </div>

                            {viewingManager.user_id && (
                              <div className="col-xl-6">
                                <div className="mb-15">
                                  <label className="form-label fw-semibold text-muted">
                                    User ID
                                  </label>
                                  <p className="fs-16">
                                    {viewingManager.user_id || viewingManager.id}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          data-bs-dismiss="modal"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* end view HR Manager modal */}
              </>
            </div>
          </div>
        </div>
        {/* app-content-area-end */}
      </>
    </div>
  );
};

export default HRManagers;
